/**
 * Pure payload normalizers for InterpreterQueueService WebSocket messages.
 */
import { e164Phone, profilePhotoPath } from '../vrs-layout/profilePhoto';
import { callTypeValue, isRecord, normalizeInterpreterStatus, numberValue, stringValue } from './queueServiceUtils';
const EXTERNAL_MEETING_PLATFORMS = ['zoom', 'meet', 'teams', 'webex'];
/** { id, name } people in a group-call message (p2p_ringing, p2p_group_no_answer); anything else is dropped. */
export function normalizeCallPeers(value) {
    return Array.isArray(value) ? value.filter(isRecord).map(peer => ({
        id: stringValue(peer.id),
        name: stringValue(peer.name)
    })) : undefined;
}
/** The client's own Zoom/Meet/Teams/Webex meeting, when a request or call names one. */
export function normalizeExternalMeeting(value) {
    if (!isRecord(value) || !EXTERNAL_MEETING_PLATFORMS.includes(value.platform)) {
        return undefined;
    }
    return {
        host: stringValue(value.host),
        platform: value.platform,
        url: stringValue(value.url)
    };
}
export function normalizeQueueStatus(data) {
    const source = isRecord(data) ? data : {};
    const activeInterpreters = Array.isArray(source.activeInterpreters)
        ? source.activeInterpreters.filter(isRecord).map(interpreter => ({
            id: stringValue(interpreter.id) || '',
            languages: Array.isArray(interpreter.languages)
                ? interpreter.languages.filter((language) => typeof language === 'string')
                : [],
            name: stringValue(interpreter.name) || 'Interpreter',
            status: normalizeInterpreterStatus(interpreter.status)
        }))
        : [];
    const pendingRequests = Array.isArray(source.pendingRequests)
        ? source.pendingRequests.filter(isRecord).map(request => ({
            callerIdPhone: stringValue(request.callerIdPhone),
            callType: callTypeValue(request.callType),
            clientName: stringValue(request.clientName) || 'Client',
            corporateAccountId: stringValue(request.corporateAccountId),
            costCenter: stringValue(request.costCenter),
            department: stringValue(request.department),
            id: stringValue(request.id) || stringValue(request.requestId) || '',
            industryTag: stringValue(request.industryTag),
            instructions: stringValue(request.instructions),
            language: stringValue(request.language) || 'ASL',
            position: numberValue(request.position),
            requestKind: request.requestKind === 'inbound_hearing' ? 'inbound_hearing' : 'standard',
            roomName: stringValue(request.roomName),
            scheduledSessionId: stringValue(request.scheduledSessionId),
            scheduledStartAt: stringValue(request.scheduledStartAt),
            targetPhone: stringValue(request.targetPhone),
            targetPhoneLast4: stringValue(request.targetPhoneLast4),
            timestamp: numberValue(request.timestamp),
            vcoEnabled: request.vcoEnabled === true,
            externalMeeting: normalizeExternalMeeting(request.externalMeeting),
            demoMode: request.demoMode === true
        }))
        : [];
    const count = (value) => typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined;
    return {
        activeInterpreters,
        // Interpreters only: waiting requests they could take (ends the breather early).
        eligibleQueueSize: count(source.eligibleQueueSize),
        pendingRequests,
        // Waiting requests this recipient's tenant/region can see (count only).
        queueSize: count(source.queueSize),
        totalMatches: typeof source.totalMatches === 'number' ? source.totalMatches : 0,
        paused: Boolean(source.paused)
    };
}
/**
 * The call-back block of an interpreted call (docs/interpreted-group-calls.md):
 * offered on the interpreter's call_ended after the anchor dropped, and named
 * on a call-back room's meeting_initiated and rings.
 */
export function normalizeAnchorCallback(value) {
    if (!isRecord(value)) {
        return undefined;
    }
    return {
        ...value,
        available: value.available === true,
        anchorName: stringValue(value.anchorName),
        rosterCount: numberValue(value.rosterCount),
        expiresAt: typeof value.expiresAt === 'string' && Number.isFinite(Date.parse(value.expiresAt)) ? value.expiresAt : undefined,
        parentCallId: stringValue(value.parentCallId),
        attempt: numberValue(value.attempt)
    };
}
export function normalizeMatchPayload(data) {
    const source = isRecord(data) ? data : {};
    return {
        callerIdPhone: stringValue(source.callerIdPhone),
        callId: stringValue(source.callId),
        callType: source.callType === 'vri' || source.callType === 'vrs' ? source.callType : undefined,
        clientId: stringValue(source.clientId),
        clientName: stringValue(source.clientName),
        clientStartWithVideoMuted: typeof source.clientStartWithVideoMuted === 'boolean'
            ? source.clientStartWithVideoMuted
            : undefined,
        durationMinutes: numberValue(source.durationMinutes),
        endedBy: stringValue(source.endedBy),
        anchorId: stringValue(source.anchorId),
        anchorName: stringValue(source.anchorName),
        callback: normalizeAnchorCallback(source.callback),
        endedByName: stringValue(source.endedByName),
        endedByRole: stringValue(source.endedByRole),
        endReason: stringValue(source.endReason),
        interpreterId: stringValue(source.interpreterId),
        interpreterName: stringValue(source.interpreterName),
        language: stringValue(source.language),
        participantId: stringValue(source.participantId),
        reason: stringValue(source.reason),
        requestKind: source.requestKind === 'inbound_hearing' ? 'inbound_hearing' : 'standard',
        requestId: stringValue(source.requestId),
        roomName: stringValue(source.roomName),
        surface: stringValue(source.surface),
        targetPhone: stringValue(source.targetPhone),
        targetPhoneLast4: stringValue(source.targetPhoneLast4),
        vcoEnabled: source.vcoEnabled === true,
        externalMeeting: normalizeExternalMeeting(source.externalMeeting),
        phoneLeg: normalizePhoneLeg(source.phoneLeg),
        transferId: stringValue(source.transferId),
        transferredToName: stringValue(source.transferredToName),
        ...source.phoneLegMoved === true ? { phoneLegMoved: true } : {}
    };
}
/** A transferred call's live phone leg (vrs-server/lib/voice-leg-handover.js), or nothing. */
function normalizePhoneLeg(value) {
    if (!isRecord(value))
        return undefined;
    const callSid = stringValue(value.callSid);
    const conferenceName = stringValue(value.conferenceName);
    return callSid && conferenceName ? { callSid, conferenceName, sessionId: stringValue(value.sessionId) } : undefined;
}
export function normalizeInterpreterRequest(data) {
    const source = isRecord(data) ? data : {};
    return {
        callerIdPhone: stringValue(source.callerIdPhone),
        callType: callTypeValue(source.callType),
        clientName: stringValue(source.clientName) || 'Client',
        corporateAccountId: stringValue(source.corporateAccountId),
        costCenter: stringValue(source.costCenter),
        department: stringValue(source.department),
        id: stringValue(source.id) || stringValue(source.requestId) || '',
        industryTag: stringValue(source.industryTag),
        instructions: stringValue(source.instructions),
        language: stringValue(source.language) || 'ASL',
        offerId: stringValue(source.offerId),
        offerExpiresAt: stringValue(source.offerExpiresAt),
        requestKind: source.requestKind === 'inbound_hearing' ? 'inbound_hearing' : 'standard',
        roomName: stringValue(source.roomName),
        scheduledSessionId: stringValue(source.scheduledSessionId),
        scheduledStartAt: stringValue(source.scheduledStartAt),
        targetPhone: stringValue(source.targetPhone),
        targetPhoneLast4: stringValue(source.targetPhoneLast4),
        timestamp: numberValue(source.timestamp),
        vcoEnabled: source.vcoEnabled === true,
        externalMeeting: normalizeExternalMeeting(source.externalMeeting),
        demoMode: source.demoMode === true
    };
}
export function normalizeRequestQueued(data) {
    const source = isRecord(data) ? data : {};
    return {
        ...source,
        position: numberValue(source.position),
        requestId: stringValue(source.requestId)
    };
}
export function normalizeVriInvitePrepared(data) {
    const source = isRecord(data) ? data : {};
    return {
        requestId: stringValue(source.requestId),
        expiresAt: stringValue(source.expiresAt),
        roomName: stringValue(source.roomName),
        status: source.status === 'live' || source.status === 'prepared' ? source.status : undefined,
        deliveries: Array.isArray(source.deliveries)
            ? source.deliveries.filter(isRecord).flatMap(delivery => {
                const channel = delivery.channel === 'email' || delivery.channel === 'sms' ? delivery.channel : null;
                const status = delivery.status === 'sent' || delivery.status === 'blocked' || delivery.status === 'failed'
                    ? delivery.status
                    : null;
                return channel && status ? [{
                        ...delivery,
                        channel,
                        errorCode: stringValue(delivery.errorCode),
                        status,
                        targetRef: stringValue(delivery.targetRef) || ''
                    }] : [];
            })
            : undefined,
        guestEmail: stringValue(source.guestEmail),
        guestName: stringValue(source.guestName),
        guestPhone: stringValue(source.guestPhone),
        inviteUrl: stringValue(source.inviteUrl),
        token: stringValue(source.token)
    };
}
export function normalizeVoicemailPayload(data) {
    const source = isRecord(data) ? data : {};
    return {
        ...source,
        calleeId: stringValue(source.calleeId),
        calleeName: stringValue(source.calleeName),
        calleePhone: stringValue(source.calleePhone),
        count: numberValue(source.count),
        durationSeconds: numberValue(source.durationSeconds),
        maxDurationSeconds: numberValue(source.maxDurationSeconds),
        message: stringValue(source.message),
        messageId: stringValue(source.messageId),
        roomName: stringValue(source.roomName),
        voicemailAvailable: typeof source.voicemailAvailable === 'boolean' ? source.voicemailAvailable : undefined
    };
}
export function normalizeNotificationPayload(data) {
    const source = isRecord(data) ? data : {};
    return {
        ...source,
        body: stringValue(source.body),
        data: isRecord(source.data) ? source.data : undefined,
        priority: source.priority === 'high' ? 'high' : 'normal',
        title: stringValue(source.title),
        type: stringValue(source.type)
    };
}
export function normalizeP2PInvitePayload(data) {
    const source = isRecord(data) ? data : {};
    return {
        ...normalizeMatchPayload(data),
        invitationId: stringValue(source.invitationId),
        conferenceInvitation: source.conferenceInvitation === true,
        expiresAt: typeof source.expiresAt === 'string' && Number.isFinite(Date.parse(source.expiresAt))
            ? source.expiresAt : undefined,
        calleeId: stringValue(source.calleeId),
        callerId: stringValue(source.callerId),
        callerName: stringValue(source.callerName),
        callerPhotoUrl: profilePhotoPath(source.callerPhotoUrl),
        callerPhone: e164Phone(source.callerPhone),
        groupCall: typeof source.groupCall === 'boolean' ? source.groupCall : undefined,
        hostId: stringValue(source.hostId),
        inboundVrs: typeof source.inboundVrs === 'boolean' ? source.inboundVrs : undefined,
        participantCount: numberValue(source.participantCount),
        // The ring ledger's ring (docs/push-ring-lifecycle.md): the id the
        // phone's system ring (CallKit / Telecom) carries, and its deadline.
        ringId: stringValue(source.ringId),
        callUUID: stringValue(source.callUUID),
        ringDeadline: numberValue(source.ringDeadline)
    };
}
const RING_END_REASON_VALUES = [
    'caller_cancelled', 'answered_elsewhere', 'declined_elsewhere', 'timeout', 'ended_for_all', 'removed', 'call_ended'
];
const RING_KIND_VALUES = [
    'p2p_direct', 'p2p_group', 'conference_invite', 'interpreter_ready', 'relay_inbound', 'interpreter_offer'
];
/** ring_ended, or null when it names no ring. An unknown reason reads as call_ended. */
export function normalizeRingEndedPayload(data) {
    const source = isRecord(data) ? data : {};
    const ringId = stringValue(source.ringId) || stringValue(source.callUUID);
    if (!ringId)
        return null;
    const reason = RING_END_REASON_VALUES.find(value => value === source.reason) || 'call_ended';
    const kind = RING_KIND_VALUES.find(value => value === source.kind);
    return {
        ringId: ringId.toUpperCase(),
        reason,
        ...(kind ? { kind } : {}),
        callId: stringValue(source.callId),
        invitationId: stringValue(source.invitationId),
        relaySessionId: stringValue(source.relaySessionId),
        roomName: stringValue(source.roomName)
    };
}
